import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { apiFormatError } from "@/lib/api";

async function compress(file) {
  if (!/^image\/(jpeg|png|webp)$/.test(file.type)) return file;
  let bitmap;
  try {
    bitmap = await createImageBitmap(file);
    const scale = Math.min(1, 2048 / Math.max(bitmap.width, bitmap.height));
    if (scale === 1) return file;
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale); canvas.height = Math.round(bitmap.height * scale);
    const context = canvas.getContext("2d");
    if (!context) return file;
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", .86));
    return blob && blob.size < file.size ? new File([blob], `${file.name.replace(/\.[^.]+$/, "")}.jpg`, { type: "image/jpeg" }) : file;
  } catch (error) { console.warn("Reference image compression skipped", error.name); return file; }
  finally { bitmap?.close(); }
}

export function useReferencePhoto(open, modelId) {
  const [photo, setPhoto] = useState(null);
  const [uploading, setUploading] = useState(false);
  const lifecycle = useRef({ active: false, controller: null, url: null, transferred: false });
  useEffect(() => {
    const current = { active: open, controller: null, url: null, transferred: false };
    lifecycle.current = current; setPhoto(null); setUploading(false);
    return () => {
      current.active = false; current.controller?.abort();
      if (current.url && !current.transferred) URL.revokeObjectURL(current.url);
    };
  }, [open, modelId]);
  const upload = async (files) => {
    const file = files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("Photo exceeds 15 MB limit."); return; }
    const current = lifecycle.current;
    current.controller = new AbortController(); setUploading(true);
    try {
      const compressed = await compress(file);
      if (!current.active) return;
      const body = new FormData(); body.append("file", compressed);
      const { data } = await api.post("/uploads", body, { signal: current.controller.signal });
      if (!current.active) return;
      current.url = URL.createObjectURL(compressed);
      setPhoto({ id: data.id, name: data.filename, previewUrl: current.url, isImage: file.type.startsWith("image/") });
      toast.success("Reference photo attached.");
    } catch (error) { if (current.active) toast.error(apiFormatError(error.response?.data?.detail)); }
    finally { if (current.active) setUploading(false); }
  };
  const remove = () => {
    if (lifecycle.current.url) URL.revokeObjectURL(lifecycle.current.url);
    lifecycle.current.url = null; setPhoto(null);
  };
  return { photo, uploading, upload, remove, transfer: () => { lifecycle.current.transferred = true; } };
}